import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_BASE_URL;

export const fetchEmployeePersonalDetailsById = createAsyncThunk(
  'employeePersonalDetail/fetchById',
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/employee/personal-details/${employeeId}`);
      return response.data;
    } catch (err) {
      if (err.response?.status === 404) return null;
      return rejectWithValue(err.response?.data?.message || "Failed to fetch details");
    }
  }
);

export const createEmployeePersonalDetails = createAsyncThunk(
  'employeePersonalDetail/create',
  async (data, { rejectWithValue }) => {
    try {
      const payload = {
        ...data,
        gender: data.gender,
        civilStatus: data.civilStatus || "Single",
      };
      const response = await axios.post(`${API_URL}/employee/personal-details`, payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message || "Creation failed");
    }
  }
);

export const updateEmployeePersonalDetails = createAsyncThunk(
  'employeePersonalDetail/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const payload = {
        ...data,
        gender: data.gender || "Other",
        civilStatus: data.civilStatus || "Single",
        religion: data.religion || "Prefer not to say",
      };
      const response = await axios.put(`${API_URL}/employee/personal-details/${id}`, payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message || "Update failed");
    }
  }
);

/**
 * Check Employee Completeness
 * Triggered after saving personal details to update the overall employee status
 */
export const checkEmployeeCompleteness = createAsyncThunk(
  'employeePersonalDetail/checkCompleteness',
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axios.patch(`${API_URL}/employee/${employeeId}/check-completeness`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Completeness check failed");
    }
  }
);

const employeePersonalDetailSlice = createSlice({
  name: 'employeePersonalDetail',
  initialState: { 
    employee: null, 
    status: 'idle', 
    completenessStatus: 'idle', // Track validation status separately
    error: null 
  },
  reducers: {
    clearEmployee: (state) => {
      state.employee = null;
      state.status = 'idle';
      state.completenessStatus = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployeePersonalDetailsById.pending, (state) => { 
        state.status = 'loading'; 
      })
      .addCase(fetchEmployeePersonalDetailsById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.employee = action.payload;
      })
      .addCase(fetchEmployeePersonalDetailsById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      
      /* Completeness Check Cases */
      .addCase(checkEmployeeCompleteness.pending, (state) => {
        state.completenessStatus = 'loading';
      })
      .addCase(checkEmployeeCompleteness.fulfilled, (state) => {
        state.completenessStatus = 'succeeded';
      })
      .addCase(checkEmployeeCompleteness.rejected, (state, action) => {
        state.completenessStatus = 'failed';
        state.error = action.payload;
      })

      /* Generic Matchers for create/update PersonalDetail */
      .addMatcher(
        (action) => action.type.endsWith('/pending') && action.type.includes('PersonalDetail'),
        (state) => { state.status = 'loading'; state.error = null; }
      )
      .addMatcher(
        (action) => action.type.endsWith('/fulfilled') && action.type.includes('PersonalDetail'),
        (state, action) => {
          state.status = 'succeeded';
          state.employee = action.payload;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith('/rejected') && action.type.includes('PersonalDetail'),
        (state, action) => {
          state.status = 'failed';
          state.error = action.payload;
        }
      );
  },
});

export const { clearEmployee } = employeePersonalDetailSlice.actions;
export default employeePersonalDetailSlice.reducer;